import { NavigationItemEntity } from '#app/modules/wordpress/entitites/navigation'
import { Icons } from '#app/components/icons'
import { cn } from '#app/utils/misc'
import { useTranslation } from 'react-i18next'
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faFacebookF,
    faTwitter,
    faInstagram,
    faYoutube,
    faGoogle,
    faXing,
    faLinkedinIn,
} from '@fortawesome/free-brands-svg-icons'
import { faCookie, faEnvelope, faLocationDot, faClock, faPhoneFlip } from '@fortawesome/pro-light-svg-icons'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { useSsrContext } from '..'

export type FooterBlockProps = {
    data: FooterData
    navigationItems: NavigationItemEntity[]
}

export type FooterData = {
    logoUrl: string
    logoAlt: string
    company: string
    email: string
    phone: string
    phoneRaw: string
    location: string
    street: string
    zip: string
    city: string
    openingHours?: string | null
    social: Record<string, string>
    textTeaser: string
    titleTeaser: string
    linkTeaser: {
        url: string
        title: string
        target: string
    }
    contactLink: {
        url: string
        title: string
        target: string
    }
    iconTeaser: string
    titleSitemap: string
}

export default function FooterBlock(props: FooterBlockProps) {
    const { t } = useTranslation()
    const context = useSsrContext()

    const socialIcons = buildSocialIcons(props.data.social)

    const navi: Record<string, Array<NavigationItemEntity>> = {
        menu: [],
        privacy: [],
    }

    let i = 0
    for (const item of props.navigationItems) {
        if (item.url == '#') {
            i++
        } else {
            if (i == 0) {
                navi.menu.push(item)
            } else if (i == 1) {
                navi.privacy.push(item)
            }
        }
    }

    const homeUrl = '/'

    return (
        <div className="relative swt-text-body-1 pt-10">
            <div className='flex items-center gap-4 md:gap-10 px-4 md:px-6'>
                <div className='h-px w-full bg-primary'></div>
                <ul className="flex list-none gap-2 p-0">
                    {socialIcons &&
                        socialIcons.map(icon => (
                            <li key={icon.link}>
                                <a
                                    className="flex size-10 md:size-12 items-center justify-center bg-background border border-primary hover:border-foreground group rounded-full"
                                    href={icon.link}
                                    aria-label={t('Follow us on') + ' ' + icon.title}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <FontAwesomeIcon
                                        className="group-hover:text-foreground h-4 w-4 text-primary"
                                        icon={icon.icon}
                                    />
                                </a>
                            </li>
                        ))}
                    <li>
                        <a
                            className="flex size-10 md:size-12 items-center justify-center bg-background border border-primary rounded-full"
                            href="#"
                            onClick={e => {
                                e.preventDefault()
                                    ; (window as any).swShowCookieNotice()
                            }}
                            aria-label={t('Reset Cookie Consent')}
                        >
                            <FontAwesomeIcon
                                className="swt-a-hover h-4 w-4 text-primary"
                                icon={faCookie}
                            />
                        </a>
                    </li>
                </ul>
                <div className='h-px w-full bg-primary'></div>
            </div>

            <div className="relative mx-auto flex max-w-screen-2xl flex-col gap-10 px-6 py-12 lg:py-16 xl:py-20 xl:gap-16">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:flex xl:flex-wrap justify-between gap-10 md:gap-16">
                    <div className='flex flex-col gap-10 justify-between'>
                        <a href={homeUrl} className='inline-flex w-auto'>
                            {props.data.logoUrl && (
                                <img src={props.data.logoUrl} alt={props.data.logoAlt} className='h-12 md:h-16 2xl:h-[72px] w-auto' />
                            )}
                        </a>

                        <div className='flex w-auto'>
                            {/* <!-- ProvenExpert Bewertungssiegel --> */}
                            <a href="https://www.provenexpert.com/baumgarten-immobilien-gmbh-co-kg/?utm_source=Widget&amp;utm_medium=Widget&amp;utm_campaign=Widget" title="Kundenbewertungen &amp; Erfahrungen zu Baumgarten Immobilien GmbH & Co KG. Mehr Infos anzeigen." target="_blank" rel="noopener noreferrer">
                                <img src="https://images.provenexpert.com/69/9d/487d6bff9ccab59a83e64a80cb94/widget_landscape_180_de_0.png" alt="Erfahrungen &amp; Bewertungen zu Baumgarten Immobilien GmbH & Co KG" width="180" height="150" />
                            </a>
                            {/* <!-- ProvenExpert Bewertungssiegel —> */}
                        </div>

                    </div>

                    <div className="flex flex-col gap-4">
                        <h3 className='m-0 swt-subheading-line text-primary'>
                            {t('Contact')}
                        </h3>
                        {props.data.street && props.data.zip && props.data.city && (
                            <div className="flex gap-2">
                                <FontAwesomeIcon
                                    className="group-hover:text-inherit size-4 mr-3 mt-1.5 text-primary"
                                    icon={faLocationDot}
                                />
                                {props.data.street} , {props.data.zip} {props.data.city}
                            </div>
                        )}
                        {props.data.openingHours && (
                            <div className="flex gap-2 whitespace-pre-line">
                                <FontAwesomeIcon
                                    className="group-hover:text-inherit size-4 mr-3 mt-1.5 text-primary"
                                    icon={faClock}
                                />
                                {props.data.openingHours}
                            </div>
                        )}
                        {props.data.phone && props.data.phoneRaw && (
                            <a href={`tel:${props.data.phoneRaw}`}
                                className="hover:text-primary text-foreground flex gap-2 group">
                                <FontAwesomeIcon
                                    className="group-hover:text-inherit size-4 mr-3 mt-1.5  text-primary"
                                    icon={faPhoneFlip}
                                />
                                <span className='font-semibold'>
                                    {t('Call Now')}!
                                </span>
                                {props.data.phone}
                            </a>
                        )}
                        {props.data.contactLink.url &&
                            (
                                <a
                                    href={props.data.contactLink.url}
                                    className="swt-a-hover text-primary flex items-center gap-2 group font-semibold"
                                >
                                    <FontAwesomeIcon
                                        className="group-hover:text-inherit size-4 mr-3 text-primary"
                                        icon={faEnvelope}
                                    />
                                    {props.data.contactLink.title}
                                    <FontAwesomeIcon
                                        className="group-hover:text-inherit size-3.5 text-inherit"
                                        icon={faArrowRight}
                                    />
                                </a>
                            )}
                    </div>

                    <div className="flex flex-col gap-4 max-w-[400px]">
                        <h3 className='m-0 swt-subheading-line text-primary'>
                            {props.data.titleTeaser}
                        </h3>
                        <div>
                            {props.data.textTeaser}
                        </div>
                        {props.data.linkTeaser.url && props.data.linkTeaser.title && (
                            <a
                                href={props.data.linkTeaser.url}
                                className="swt-a-hover text-primary flex items-center gap-2 group font-semibold"
                            >
                                <div className='fill-primary group-hover:fill-inherit size-4 mr-3' dangerouslySetInnerHTML={{ __html: props.data.iconTeaser }}></div>
                                {props.data.linkTeaser.title}
                                <FontAwesomeIcon
                                    className="group-hover:text-inherit size-3.5 text-inherit"
                                    icon={faArrowRight}
                                />
                            </a>
                        )}
                    </div>

                    <div className="flex flex-col gap-4">
                        <h3 className='m-0 swt-subheading-line text-primary'>
                            {props.data.titleSitemap}
                        </h3>
                        {navi.menu && (
                            <ul className="m-0 swt-text-body-1 list-none flex flex-col gap-4">
                                {navi.menu.map(item => (
                                    <li key={item.id}>
                                        <a
                                            href={item.url}
                                            target={item.target}
                                            title={item.title}
                                            className="swt-a-hover text-foreground"
                                        >
                                            {item.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}

                    </div>
                </div>
            </div>

            <div className="mx-6 border-t border-neutral-100 flex items-center justify-center p-8 swt-text-caption">
                {navi.privacy && navi.privacy.length > 0 && (
                    <ul className="flex items-center list-none gap-4 p-0">
                        {navi.privacy.map(item => (
                            <>
                                <li key={item.id}>
                                    <a
                                        href={item.url}
                                        target={item.target}
                                        title={item.title}
                                        className="swt-a-hover text-foreground"
                                    >
                                        {item.title}
                                    </a>
                                </li>
                                <li className='last:hidden'>
                                    <span className='swt-deco-svg w-3 h-3 flex items-center justify-center'></span>
                                </li>
                            </>
                        ))}
                    </ul>
                )}
            </div>
        </div >
    )
}

function buildSocialIcons(social: Record<string, string>) {
    const icons: Array<{ link: string; icon: IconDefinition; title: string }> = []

    if (social.facebook) {
        icons.push({
            link: social.facebook,
            icon: faFacebookF,
            title: 'Facebook',
        })
    }

    if (social.twitter) {
        icons.push({
            link: social.twitter,
            icon: faTwitter,
            title: 'Twitter',
        })
    }

    if (social.instagram) {
        icons.push({
            link: social.instagram,
            icon: faInstagram,
            title: 'Instagram',
        })
    }

    if (social.linkedin) {
        icons.push({
            link: social.linkedin,
            icon: faLinkedinIn,
            title: 'LinkedIn',
        })
    }

    if (social.xing) {
        icons.push({
            link: social.xing,
            icon: faXing,
            title: 'Xing',
        })
    }

    if (social.youtube) {
        icons.push({
            link: social.youtube,
            icon: faYoutube,
            title: 'YouTube',
        })
    }

    return icons
}
